/**
 * State factory and high-level reducers that compose all the other modules.
 *
 * These are the public mutation entry points. Always pass through
 * `applyTap` / `performCrystallization` rather than building state by hand —
 * they keep stats, atlas, lessons, and activation events in sync.
 */
import { selectActivation, type ActivationEvent } from './activations.js';
import { addToAtlas, computeAtlasBonus, computeAtlasProgress } from './atlas.js';
import { canAdvanceEpoch, previewCrystallization } from './conversion.js';
import {
  BASE_TAP_COOLDOWN_MS,
  RHYTHM_WINDOW_MS,
  getNextEpoch,
  streakMultiplier,
} from './constants.js';
import { computeLessonsBonus, evaluateLessons } from './lessons.js';
import { composeGlobalMultiplier } from './multipliers.js';
import { computeTapValue, selectPhrase } from './tap.js';
import type { RandomSource } from './rng.js';
import type {
  CrystallizationResult,
  Epoch,
  GameState,
  LessonDefinition,
  MultiplierBreakdown,
  PhraseEntry,
  ResourceState,
  SaiActivation,
  TapResult,
} from './types.js';
import {
  applyUpgradesBonus,
  computeCooldownReduction,
  computeCrystallizationBonusMultiplier,
  computeRhythmWindowExtension,
  computeStreakBurst,
  type Upgrade,
} from './upgrades.js';
import { MIN_TAP_COOLDOWN_MS } from './constants.js';

// ---------------------------------------------------------------------------
// Initial state
// ---------------------------------------------------------------------------

export function createInitialState(now: number = Date.now()): GameState {
  return {
    epoch: 1,
    resources: createEmptyResources(),
    stats: {
      totalTaps: 0,
      totalTapsRejected: 0,
      totalCrystallizations: 0,
      totalGoldenSparksCaught: 0,
      tapsInRhythmBest: 0,
      tapsInRhythmCurrent: 0,
      lastTapAt: null,
      firstTapAt: null,
      lastActiveAt: now,
      daysStreak: 0,
    },
    cooldownMs: BASE_TAP_COOLDOWN_MS,
    atlasCollected: new Map(),
    lessonsUnlocked: new Set(),
    profile: {
      displayName: null,
    },
    globalBoosts: {
      prestigeMultiplier: 1,
      silentBlessingMultiplier: 1,
    },
    purchasedUpgrades: new Map(),
  };
}

export function createEmptyResources(): ResourceState {
  return {
    awarenesses: 0,
    spirit_balls: 0,
    body_impulses: 0,
    meaning_crystals: 0,
    sprouts: 0,
    frequencies: 0,
    unlock_keys: 0,
    seeds_of_light: 0,
    light_of_origin: 0,
  };
}

// ---------------------------------------------------------------------------
// Multiplier composition from state
// ---------------------------------------------------------------------------

export interface ComposeMultiplierInput {
  readonly state: GameState;
  readonly lessonsCatalog: readonly LessonDefinition[];
  readonly allPhrases: readonly PhraseEntry[];
  /**
   * Каталог апгрейдов. Если не передан — `upgradesBonus = 0` (нужно для
   * существующих тестов, написанных до doc 14). В production-коде всегда
   * передаём `UPGRADES_ALPHA` из `@sai-clicker/content`.
   */
  readonly upgradesCatalog?: readonly Upgrade[];
  // Future: guides, artifacts, role, mantras, season — wired here.
}

export function composeMultiplierFromState(input: ComposeMultiplierInput): MultiplierBreakdown {
  const lessonsBonus = computeLessonsBonus(input.state.lessonsUnlocked, input.lessonsCatalog);
  const atlasProgress = computeAtlasProgress(input.state.atlasCollected, input.allPhrases);
  const atlasBonus = computeAtlasBonus(atlasProgress);
  const upgradesBonus =
    input.upgradesCatalog !== undefined
      ? applyUpgradesBonus({
          purchased: input.state.purchasedUpgrades,
          catalog: input.upgradesCatalog,
          target: 'tap',
        })
      : 0;
  return composeGlobalMultiplier({
    lessonsBonus,
    atlasBonus,
    upgradesBonus,
    silentBlessing: input.state.globalBoosts.silentBlessingMultiplier,
    prestige: input.state.globalBoosts.prestigeMultiplier,
  });
}

// ---------------------------------------------------------------------------
// applyTap — the single reducer for a tap event
// ---------------------------------------------------------------------------

export interface ApplyTapInput {
  readonly state: GameState;
  readonly lessonsCatalog: readonly LessonDefinition[];
  readonly allPhrases: readonly PhraseEntry[];
  readonly activationsCatalog: readonly SaiActivation[];
  /** Optional — see ComposeMultiplierInput.upgradesCatalog. */
  readonly upgradesCatalog?: readonly Upgrade[];
  readonly firedActivationIds: ReadonlySet<string>;
  readonly now: number;
  readonly random?: RandomSource;
}

export function applyTap(input: ApplyTapInput): TapResult {
  // ---- Effective cooldown / rhythm window (doc 14 cooldownReduction +
  //      rhythmWindowExtension effects) ----
  // Apply upgrades to base values. Floor cooldown to MIN_TAP_COOLDOWN_MS
  // so upgrades can never produce zero-or-negative gap. Rhythm window
  // can grow without ceiling — bigger window = easier streak.
  const cooldownReduction =
    input.upgradesCatalog !== undefined
      ? computeCooldownReduction(input.state.purchasedUpgrades, input.upgradesCatalog)
      : 0;
  const effectiveCooldown = Math.max(
    MIN_TAP_COOLDOWN_MS,
    input.state.cooldownMs - cooldownReduction,
  );
  const rhythmExtension =
    input.upgradesCatalog !== undefined
      ? computeRhythmWindowExtension(input.state.purchasedUpgrades, input.upgradesCatalog)
      : 0;
  const effectiveRhythmWindow = RHYTHM_WINDOW_MS + rhythmExtension;

  // ---- Cooldown gate (JB 2026-05-24) ----
  // If the player tapped before the cooldown elapsed, the tap is silently
  // rejected: no state change, no awareness, no events. UI may show a
  // brief "not ready" hint but should NOT play the ding.
  const previousTapAt = input.state.stats.lastTapAt;
  if (previousTapAt !== null && input.now - previousTapAt < effectiveCooldown) {
    return {
      accepted: false,
      awarenessesGained: 0,
      phrase: null,
      state: {
        ...input.state,
        stats: {
          ...input.state.stats,
          totalTapsRejected: input.state.stats.totalTapsRejected + 1,
        },
      },
      newlyUnlockedLessons: [],
      triggeredActivation: null,
      wasInRhythm: false,
    };
  }

  // ---- Rhythm window ----
  // The tap was accepted (cooldown elapsed). Now check whether it landed
  // within the rhythm window (= cooldown + RHYTHM_WINDOW_MS). Inside the
  // window the streak extends; outside it resets to 1.
  const readyAt = previousTapAt !== null ? previousTapAt + effectiveCooldown : input.now;
  const lateBy = input.now - readyAt;
  const wasInRhythm = previousTapAt !== null && lateBy <= effectiveRhythmWindow;
  const newRhythmCurrent = wasInRhythm ? input.state.stats.tapsInRhythmCurrent + 1 : 1;
  const newRhythmBest = Math.max(input.state.stats.tapsInRhythmBest, newRhythmCurrent);

  // ---- Compute tap value ----
  const multiplier = composeMultiplierFromState(input);
  const newTotalTaps = input.state.stats.totalTaps + 1;
  const streak = streakMultiplier(newRhythmCurrent);
  const rngOption = input.random !== undefined ? { random: input.random } : {};

  const epochPhrases = input.allPhrases.filter((p) => p.epoch === input.state.epoch);
  const phrase = selectPhrase({
    tapIndex: newTotalTaps,
    availablePhrases: epochPhrases,
    // Pass sighting counts so applyAntiRepeat dampens phrases the player
    // has already seen many times. Doc 28 §3 — variety without erasure.
    sightingCounts: input.state.atlasCollected,
    ...rngOption,
  });

  // Layer-specific upgrades — apply ONLY for taps whose phrase matches that
  // layer. JB 2026-05-26 mechanics audit: U011 (body), U012 (awareness) etc.
  // были «мёртвые», composeMultiplierFromState считает только generic 'tap'
  // bonus. Здесь добавляем layer-specific bonus поверх globalMultiplier.
  const layerBonus =
    input.upgradesCatalog !== undefined
      ? 1 +
        applyUpgradesBonus({
          purchased: input.state.purchasedUpgrades,
          catalog: input.upgradesCatalog,
          target: 'tap',
          layer: phrase.layer,
        })
      : 1;

  // Streak burst — doc 27 §2. Каждый Nth тап в стрике даёт ×K бонус.
  // Применяется только если игрок прямо сейчас на N-м тапе в стрике
  // (newRhythmCurrent % everyNth === 0). Лорно: ритм «выплёскивает»
  // накопленную энергию.
  let burstMultiplier = 1;
  if (input.upgradesCatalog !== undefined && newRhythmCurrent > 0) {
    const burst = computeStreakBurst(input.state.purchasedUpgrades, input.upgradesCatalog);
    if (burst !== null && newRhythmCurrent % burst.everyNth === 0) {
      burstMultiplier = burst.multiplier;
    }
  }

  const specialOption = phrase.special !== undefined ? { special: phrase.special } : {};
  const tap = computeTapValue({
    epoch: input.state.epoch,
    globalMultiplier: multiplier.total * layerBonus * burstMultiplier,
    phraseLayer: phrase.layer,
    ...specialOption,
    streak,
    ...rngOption,
  });

  const atlasResult = addToAtlas(input.state.atlasCollected, phrase.id);

  const nextState: GameState = {
    ...input.state,
    resources: {
      ...input.state.resources,
      awarenesses: input.state.resources.awarenesses + tap.value,
    },
    stats: {
      ...input.state.stats,
      totalTaps: newTotalTaps,
      lastTapAt: input.now,
      firstTapAt: input.state.stats.firstTapAt ?? input.now,
      lastActiveAt: input.now,
      tapsInRhythmCurrent: newRhythmCurrent,
      tapsInRhythmBest: newRhythmBest,
    },
    atlasCollected: atlasResult.atlas,
  };

  const newlyUnlocked = evaluateLessons({
    state: nextState,
    catalog: input.lessonsCatalog,
    allPhrases: input.allPhrases,
    now: input.now,
  });

  let stateAfterLessons = nextState;
  if (newlyUnlocked.length > 0) {
    const nextUnlocked = new Set(nextState.lessonsUnlocked);
    for (const lesson of newlyUnlocked) {
      nextUnlocked.add(lesson.id);
    }
    stateAfterLessons = { ...nextState, lessonsUnlocked: nextUnlocked };
  }

  const event: ActivationEvent = { kind: 'tap', newTotalTaps };
  const triggeredActivation = selectActivation(input.activationsCatalog, input.firedActivationIds, {
    state: stateAfterLessons,
    event,
  });

  return {
    accepted: true,
    awarenessesGained: tap.value,
    phrase,
    state: stateAfterLessons,
    newlyUnlockedLessons: newlyUnlocked,
    triggeredActivation,
    wasInRhythm,
  };
}

// ---------------------------------------------------------------------------
// performCrystallization — convert awarenesses to spirit balls
// ---------------------------------------------------------------------------

export interface PerformCrystallizationInput {
  readonly state: GameState;
  readonly lessonsCatalog: readonly LessonDefinition[];
  readonly allPhrases: readonly PhraseEntry[];
  readonly activationsCatalog: readonly SaiActivation[];
  /** Optional — when provided, applies crystallizationBonus from purchased upgrades. */
  readonly upgradesCatalog?: readonly Upgrade[];
  readonly firedActivationIds: ReadonlySet<string>;
  readonly now: number;
}

export function performCrystallization(input: PerformCrystallizationInput): CrystallizationResult {
  const preview = previewCrystallization(input.state.resources.awarenesses, input.state.epoch);
  if (preview.spiritBallsToGain === 0) {
    // Nothing to do — return a no-op result that still advances `lastActiveAt`.
    return {
      spiritBallsGained: 0,
      state: {
        ...input.state,
        stats: { ...input.state.stats, lastActiveAt: input.now },
      },
      newlyUnlockedLessons: [],
      triggeredActivation: null,
      didAdvanceEpoch: false,
      newEpoch: input.state.epoch,
    };
  }

  // doc 14 crystallizationBonus — каждый матчящий апгрейд множит итог.
  const crystallizeBonus =
    input.upgradesCatalog !== undefined
      ? computeCrystallizationBonusMultiplier(input.state.purchasedUpgrades, input.upgradesCatalog)
      : 1;
  const spiritBallsGained = Math.floor(preview.spiritBallsToGain * crystallizeBonus);
  const newSpiritBalls = input.state.resources.spirit_balls + spiritBallsGained;
  let newEpoch: Epoch = input.state.epoch;
  let didAdvanceEpoch = false;

  if (canAdvanceEpoch(newSpiritBalls, input.state.epoch)) {
    const next = getNextEpoch(input.state.epoch);
    if (next !== null) {
      newEpoch = next;
      didAdvanceEpoch = true;
    }
  }

  const newState: GameState = {
    ...input.state,
    epoch: newEpoch,
    resources: {
      ...input.state.resources,
      awarenesses: 0,
      spirit_balls: newSpiritBalls,
    },
    stats: {
      ...input.state.stats,
      totalCrystallizations: input.state.stats.totalCrystallizations + 1,
      lastActiveAt: input.now,
    },
  };

  const newlyUnlocked = evaluateLessons({
    state: newState,
    catalog: input.lessonsCatalog,
    allPhrases: input.allPhrases,
    now: input.now,
  });

  let stateAfterLessons = newState;
  if (newlyUnlocked.length > 0) {
    const nextUnlocked = new Set(newState.lessonsUnlocked);
    for (const lesson of newlyUnlocked) {
      nextUnlocked.add(lesson.id);
    }
    stateAfterLessons = { ...newState, lessonsUnlocked: nextUnlocked };
  }

  const event: ActivationEvent = {
    kind: 'crystallization',
    didAdvanceEpoch,
    newEpoch,
  };
  const triggeredActivation = selectActivation(input.activationsCatalog, input.firedActivationIds, {
    state: stateAfterLessons,
    event,
  });

  return {
    spiritBallsGained,
    state: stateAfterLessons,
    newlyUnlockedLessons: newlyUnlocked,
    triggeredActivation,
    didAdvanceEpoch,
    newEpoch,
  };
}
